import {
  expandChatOverlay,
  hideChatBubble,
  initialize,
  MessagingTopic,
  Language,
  Partner,
  Channel,
  showChatBubble,
  attachSDKToContainer,
  injectScript,
  LoggedInEventArgs,
  SessionChangedEventArgs,
  MessageSendFailedEventArgs,
  MessageSentEventArgs,
  NewMessageEventArgs,
  MessagingOverlayStateChangedEventArgs,
} from "@soluto-private/remote-expert-sdk";
import { trackEvent, updateStickyProps } from "@trailhead/analytics";
import { EventEmitter } from "events";

const TWILIO_CHAT_SDK_SRC = <string>process.env.REACT_APP_TWILIO_CHAT_SDK_SRC;

const eventsHandler = (eventEmitter: EventEmitter) => {
  eventEmitter.on("LOGIN_FAILED", (args: { error: string }) => {
    trackEvent("ExpertChatSessionMessageSendLogOutFailed", {
      ...args,
      chatType: "RemoteExpert",
    });
  });

  eventEmitter.on("LOGGED_IN", (args: LoggedInEventArgs) => {
    trackEvent("UserLoggedInToExpertChat", { chatIdentity: args.identity });
    updateStickyProps({ chatIdentity: args.identity });
  });

  eventEmitter.on("SESSION_CHANGED", (args: SessionChangedEventArgs) => {
    trackEvent("ExpertChatSessionChanged", {
      expertChatSessionAction: args.action,
      expertChatSessionExpertise: args.expertise,
      expertChatSessionPool: args.pool,
      expertChatSessionId: args.sessionId,
      expertChatExpertId: args.expertId,
    });
    updateStickyProps({
      expertChatSessionAction: args.action,
      expertChatSessionExpertise: args.expertise,
      expertChatSessionPool: args.pool,
      expertChatSessionId: args.sessionId,
      expertChatExpertId: args.expertId,
    });
  });

  eventEmitter.on("SEND_MESSAGE_FAILED", (args: MessageSendFailedEventArgs) => {
    trackEvent("ExpertChatSessionMessageSendFailed", {
      ...args,
    });
  });

  eventEmitter.on("MESSAGE_SENT", (args: MessageSentEventArgs) => {
    trackEvent("ExpertChatSessionMessageSent", {
      messageType: args.type,
      expertChatSessionId: args.sessionId,
    });
  });

  eventEmitter.on("MESSAGE_RECEIVED", (args: NewMessageEventArgs) => {
    trackEvent("ExpertChatSessionMessageReceived", {
      messageType: args.type,
      message: args.message,
    });
  });

  eventEmitter.on(
    "FABIO_CLICKED",
    (args: MessagingOverlayStateChangedEventArgs) => {
      trackEvent("ExpertChatBubbleClicked", { isOpen: args.isExpanded });
    },
  );
};

export class TwilioClient {
  private static async _init(
    partner: string,
    _expertise: string,
    onLoadCompleteCallback: () => void,
  ): Promise<void> {
    await injectScript(
      {
        id: "remote-expert-sdk",
        src: TWILIO_CHAT_SDK_SRC,
      },
      async () => {
        try {
          await initialize({
            partner: partner as Partner,
            messagingTopic: MessagingTopic.DEVICE_REPLACEMENT_ONBOARDING,
            appName: "Device Setup",
            language: Language.EN_US,
            registerToEvents: eventsHandler,
            channel: Channel.CHAT,
            environment:
              process.env.NODE_ENV === "production"
                ? "production"
                : "development",
          });
          this.showMessaging();
          onLoadCompleteCallback();
        } catch (e) {
          console.log(`Error initializing Twilio Chat SDK: ${e as string}`);
        }
      },
    );
  }

  static async init(
    partner: string,
    expertise: string[],
    onLoadCompleteCallback: () => void,
  ): Promise<void> {
    if (typeof partner === "undefined") {
      return;
    }

    await this._init(partner, expertise[0], onLoadCompleteCallback);
  }

  static showMessaging(): void {
    try {
      showChatBubble();
    } catch (e) {
      console.log(`Twilio SDK ERROR: ${e as string}`);
    }
  }

  static hideMessaging(): void {
    try {
      hideChatBubble();
    } catch (e) {
      console.log(`Twilio SDK ERROR: ${e as string}`);
    }
  }

  static openMessagingOverlay(message?: string): void {
    try {
      expandChatOverlay({ message });
    } catch (e) {
      console.log(`Twilio SDK ERROR: ${e as string}`);
    }
  }

  static sendHiddenMessage(message: string): void {
    this.openMessagingOverlay(message);
  }

  static attachToContainer(containerId: string): void {
    try {
      attachSDKToContainer(containerId);
    } catch (e) {
      console.log(`Twilio SDK ERROR: ${e as string}`);
    }
  }
}

export type TwilioClientType = typeof TwilioClient;
